import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { LogoMark } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const caseId = params.get("case_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!caseId) {
      setState("failed");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/cases/${caseId}`);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) {
        // keep polling briefly
      }
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
  }, [caseId]);

  return (
    <main className="pt-[68px] min-h-[80vh] flex items-center justify-center px-4">
      <motion.div
        data-testid="payment-success-card"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white border border-[#E5E7EB] rounded-[2rem] p-10 text-center shadow-xl"
      >
        <LogoMark size={48} className="rounded-2xl mx-auto mb-6" />
        {state === "checking" && (
          <>
            <Loader2 size={40} className="animate-spin text-[#2563EB] mx-auto mb-4" />
            <h1 className="font-display font-semibold text-2xl mb-2">
              Confirming your payment...
            </h1>
            <p className="text-[#52525B] text-sm">
              Hold tight, this takes a few seconds.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={56} className="text-[#2563EB] mx-auto mb-4" strokeWidth={1.5} />
            <h1
              data-testid="payment-confirmed-heading"
              className="font-display font-semibold text-3xl mb-3"
            >
              Paid. Maximum NLL unlocked.
            </h1>
            <p className="text-[#52525B] leading-relaxed">
              Your payment went through and your case is officially in motion.
              An NLL Agent will be in touch by email with next steps.
            </p>
            <Link
              to="/"
              data-testid="payment-home-link"
              className="inline-block mt-7 bg-[#111827] text-[#FFFFFF] font-semibold px-8 py-3 rounded-full hover:bg-[#1F2937] transition-colors"
            >
              Back to home
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={56} className="text-[#a1a1aa] mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-display font-semibold text-2xl mb-3">
              We couldn&rsquo;t confirm that payment
            </h1>
            <p className="text-[#52525B] text-sm leading-relaxed">
              If money left your account, it will be confirmed automatically
              within a few minutes. Otherwise no charge was made.
            </p>
            <Link
              to="/"
              className="inline-block mt-7 bg-[#111827] text-[#FFFFFF] font-semibold px-8 py-3 rounded-full hover:bg-[#1F2937] transition-colors"
            >
              Back to home
            </Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
